import { getColumnId, getColumnsWithPinnedProp, useTableHideAndPinSettings } from 'components/TableSettings';
import { ActKc2GroupTypeEnum } from 'gql/graphql';
import { useCommonActsKC2GroupDelete } from 'pages/Project/Common/ActsKC2ListContainer/hooks';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { uerRemoveWorkOrderActsKC2GroupGql } from '../uerWorkOrderActsKc2ListQueries';

import { useGetUerActsKc2List } from './useGetUerActsKc2List';
import { useUerActsKc2ListColumns } from './useUerActsKc2ListColumns';

export const useUerActsKc2ListData = ({
    activeGroupTypeId,
    workOrderId,
    defaultOpenedSubGroup,
}: {
    activeGroupTypeId: ActKc2GroupTypeEnum | undefined | null;
    workOrderId: number;
    defaultOpenedSubGroup?: string;
}) => {
    const [groupEditId, setGroupEditId] = useState<number | null>(null);

    const handlerOpenEditGroup = useCallback((id: number) => {
        setGroupEditId(id);
    }, []);

    const handlerCloseEditGroup = useCallback(() => {
        setGroupEditId(null);
    }, []);

    const {
        actKc2List,
        refreshActListData,
        fetchedDataCount,
        totalItemsCount,
        groupState,
        changeGroupState,
        changeFilterHandler,
        changeOrderHandler,
    } = useGetUerActsKc2List({
        workOrderId,
        groupType: activeGroupTypeId,
        defaultOpenedSubGroup,
    });

    const noRowsTemplate = useMemo(() => {
        if (activeGroupTypeId === ActKc2GroupTypeEnum.Custom && !actKc2List.length) {
            return 'Для отображения данных cформируйте группу';
        }

        return 'Нет данных';
    }, [actKc2List.length, activeGroupTypeId]);

    const { isDeleteOpened, confirmDeletion, cancelDeletion, handleDeleteGroup } = useCommonActsKC2GroupDelete({
        refreshActListData,
        deleteQuery: uerRemoveWorkOrderActsKC2GroupGql,
    });

    const tableColumns = useUerActsKc2ListColumns({
        workOrderId,
        groupState,
        changeGroupState,
        handleDeleteGroup,
        handlerOpenEditGroup,
        changeFilterHandler,
        activeGroupTypeId,
    });

    const { tableSettingsConfirmHandleCreator, isColumnVisible, pinnedItems } = useTableHideAndPinSettings({
        tableID: 'uerActKc2List',
    });

    const tableSettingsColumns = useMemo(
        () =>
            tableColumns.reduce((acc: ColDef<any>[], column, idx, fullArr) => {
                if (!('field' in column && (column.field as any) === 'menu')) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean; isPinDefDisabled: boolean };

                    if (idx < 4) {
                        newColumn.disabled = true;
                    }

                    if (idx === fullArr.length - 1) {
                        newColumn.disabled = true;
                        newColumn.isPinDefDisabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [pinnedItems, tableColumns],
    );

    const handleTableSettingsConfirm = useMemo(
        () => tableSettingsConfirmHandleCreator(tableSettingsColumns),
        [tableSettingsColumns, tableSettingsConfirmHandleCreator],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns: tableColumns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [isColumnVisible, pinnedItems, tableColumns]);

    return {
        actKc2List,
        tableColumns: fullSetColumns,
        noRowsTemplate,
        totalItemsCount,
        fetchedDataCount,
        refreshActListData,
        isDeleteOpened,
        confirmDeletion,
        cancelDeletion,
        groupEditId,
        handlerCloseEditGroup,
        changeOrderHandler,
        tableSettingsColumns,
        handleTableSettingsConfirm,
        isColumnVisible,
    };
};
